import { DragEvent, FC, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface ImageDropZoneProps {
  label?: string
  initialUrl?: string
  onChange?: (file: File | null) => void
  error?: string
  disabled?: boolean
}

export const ImageDropZone: FC<ImageDropZoneProps> = ({
  label,
  initialUrl,
  onChange,
  error,
  disabled = false,
}) => {
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const displaySrc = localPreview || initialUrl || null

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => setLocalPreview(e.target?.result as string)
    reader.readAsDataURL(file)
    onChange?.(file)
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDrop={disabled ? undefined : onDrop}
        onDragOver={disabled ? undefined : onDragOver}
        onDragLeave={disabled ? undefined : () => setIsDragging(false)}
        style={{
          backgroundColor: isDragging ? '#e8f4fd' : '#f1f3f5',
          borderRadius: '8px',
          padding: '12px 16px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minWidth: '170px',
          transition: 'background-color 0.15s',
          userSelect: 'none',
        }}
      >
        <input
          ref={inputRef}
          type='file'
          accept='image/*'
          disabled={disabled}
          className='d-none'
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleFile(file)
            e.target.value = ''
          }}
        />
        {displaySrc ? (
          <div
            style={{ position: 'relative', flexShrink: 0, width: 36, height: 36 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={displaySrc}
              alt='preview'
              style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }}
            />
            <button
              type='button'
              onClick={() => setPreviewOpen(true)}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                border: 'none',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0,
                transition: 'opacity 0.15s',
                padding: 0,
              }}
            >
              <i className='bi bi-eye-fill' style={{ color: '#fff', fontSize: '0.8rem' }} />
            </button>
          </div>
        ) : (
          <i
            className='bi bi-image text-muted'
            style={{ fontSize: '1.25rem', flexShrink: 0, opacity: disabled ? 0.5 : 1 }}
          />
        )}
        <div style={{ opacity: disabled ? 0.5 : 1 }}>
          {label && (
            <div style={{ color: '#0066cc', fontWeight: 600, fontSize: '13px', lineHeight: 1.4 }}>
              {label}
            </div>
          )}
          <div className='text-muted' style={{ fontSize: '12px', lineHeight: 1.4 }}>
            Seleccionar o arrastrar
          </div>
        </div>
      </div>
      {error && (
        <span className='text-danger d-block mt-1' style={{ fontSize: '11px' }}>
          {error}
        </span>
      )}
      {previewOpen &&
        displaySrc &&
        createPortal(
          <div
            onClick={() => setPreviewOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.8)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
            >
              <img
                src={displaySrc}
                alt='preview'
                style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, display: 'block' }}
              />
              <button
                type='button'
                onClick={() => setPreviewOpen(false)}
                style={{
                  position: 'absolute',
                  top: -14,
                  right: -14,
                  width: 28,
                  height: 28,
                  background: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                }}
              >
                <i className='bi bi-x' style={{ fontSize: '1rem', lineHeight: 1 }} />
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
